import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useTranslation } from 'react-i18next';
import AnalyseForm from '../components/resume/AnalyseForm';
import ResumeCard from '../components/resume/ResumeCard';
import { resumeService, internshipService } from '../services/api';
import SkeletonLoader from '../components/ui/SkeletonLoader';

function ResumePage() {
    const { t } = useTranslation();
    const [resumes, setResumes] = useState([]);
    const [applications, setApplications] = useState([]);
    const [permanentCV, setPermanentCV] = useState(null);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [useProfileCV, setUseProfileCV] = useState(false);
    const [selectedAppId, setSelectedAppId] = useState('');
    const [jobDescription, setJobDescription] = useState('');

    useEffect(() => { fetchData(); }, []);

    const fetchData = async () => {
        try {
            const [resumeRes, appRes] = await Promise.all([
                resumeService.list(),
                internshipService.list(),
            ]);
            const allResumes = resumeRes.data;
            const permanent = allResumes.find(r => r.storage_type === 'permanent');
            const temporaries = allResumes.filter(r => r.storage_type === 'temporary');

            setPermanentCV(permanent || null);

            const allToShow = [...temporaries];
            if (permanent && permanent.score && permanent.score.ats_score) {
                allToShow.push(permanent);
            }
            setResumes(allToShow);
            setApplications(appRes.data);
        } catch (err) {
            setError(t('common.error'));
        } finally {
            setLoading(false);
        }
    };

    const handleAnalyse = async (e) => {
        e.preventDefault();
        let finalJobDescription = jobDescription.trim();
        if (!finalJobDescription && selectedAppId) {
            const app = applications.find(a => a.id === parseInt(selectedAppId));
            if (app) finalJobDescription = `${app.position} at ${app.company_name}`;
        }
        if (useProfileCV && !permanentCV) {
            setError(t('resume.none_uploaded'));
            return;
        }
        const formData = new FormData();
        if (useProfileCV) {
            formData.append('use_profile_cv', true);
            formData.append('resume_id', permanentCV.id);
        } else {
            const file = e.target.resume?.files[0];
            if (!file) { setError(t('common.error')); return; }
            const allowedTypes = [
                'application/pdf',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
            ];
            if (!allowedTypes.includes(file.type)) {
                setError(t('common.error'));
                return;
            }
            if (file.size > 10 * 1024 * 1024) {
                setError(t('common.error'));
                return;
            }
            formData.append('resume', file);
        }
        if (selectedAppId) formData.append('application_id', selectedAppId);
        if (finalJobDescription) formData.append('job_description', finalJobDescription);
        setUploading(true);
        setError('');
        setSuccess('');
        try {
            await resumeService.upload(formData);
            setSuccess(t('resume.analysing'));
            e.target.reset();
            setJobDescription('');
            setSelectedAppId('');
            setTimeout(() => fetchData(), 500);
        } catch (err) {
            setError(t('common.error'));
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t('common.delete_confirm'))) return;
        try {
            if (permanentCV && permanentCV.id === id) {
                setResumes(prev => prev.filter(r => r.id !== id));
            } else {
                await resumeService.delete(id);
                setResumes(prev => prev.filter(r => r.id !== id));
            }
        } catch (err) {
            setError(t('common.error'));
        }
    };

    

    if (loading) return (
        <Layout>
            <div className="page-container"><SkeletonLoader /></div>
        </Layout>
    );

    return (
        <Layout>
            <div className="page-container">
                <h1>{t('resume.title')}</h1>
                <p className="subtitle">{t('resume.subtitle')}</p>

                <AnalyseForm
                    onSubmit={handleAnalyse}
                    uploading={uploading}
                    error={error}
                    success={success}
                    useProfileCV={useProfileCV}
                    setUseProfileCV={setUseProfileCV}
                    permanentCV={permanentCV}
                    selectedAppId={selectedAppId}
                    setSelectedAppId={setSelectedAppId}
                    jobDescription={jobDescription}
                    setJobDescription={setJobDescription}
                    applications={applications}
                />

                <div className="section">
                    <h2>{t('resume.history')}</h2>
                    {resumes.length === 0 ? (
                        <p className="empty-state">
                            {t('resume.no_analyses')}
                        </p>
                    ) : (
                        <div className="resumes-list">
                            {resumes.map(resume => (
                                <ResumeCard
                                    key={resume.id}
                                    resume={resume}
                                    showDelete={true}
                                    onDelete={handleDelete}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
}

export default ResumePage;